import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { readPage } from '../../../utils/apiResult';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';

// Triage flag for boomerang applicants (same person re-applying). Amber for a
// prior application, red when a prior ended in disqualification/rejection/withdrawal.
const ReapplyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ $danger }) => ($danger ? '#FDECEA' : '#FFF3CD')};
  color: ${({ $danger }) => ($danger ? '#C62828' : '#856404')};
`;

// EO-12 — "işe alım tamamlanmadı" rozeti. Teklif `accepted`, kadro `filled`, ama Employee
// açılmadı (e-posta çakışması / iç aday) ya da kurumsal adres hâlâ girilmedi. Eskiden bunun
// TEK izi API log'undaki bir uyarı satırıydı; İK hiçbir ekranda göremiyordu.
const HireFlagBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ $danger }) => ($danger ? '#FDECEA' : '#E7F1FF')};
  color: ${({ $danger }) => ($danger ? '#C62828' : '#0B5FA5')};
`;

// `automationFlagsJson` serbest bir JSON metnidir (bot işareti, mükerrer skoru, işe alım
// izleri). Bozuk/boş değer ekranı ÖLDÜRMEZ — boş nesneye düşülür.
export const parseAutomationFlags = (raw) => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch { return {}; }
};

export const APPLICATION_STATUSES = [
  'active',
  'on_hold',
  'withdrawn',
  'disqualified',
  'offered',
  'accepted',
  'rejected',
];

// Same set as candidate sources — reused for the joined-source filter on this list.
export const APPLICATION_SOURCES = [
  'linkedin',
  'indeed',
  'kariyernet',
  'yenibiris',
  'career_site',
  'referral',
  'external_agency',
  'talent_pool',
  'boomerang',
];

// `filterKey` verilmezse parseTableParams `${kolonAdı}[in]` üretir (status/source
// için doğru). Kolon adı sunucu alanından AYRIŞTIĞINDA (currentStageName →
// currentStageId[in]) anahtar açıkça verilmeli; aksi halde filtre sunucuya
// `currentStageName[in]` diye gider ve SESSİZCE düşer.
const multiSelectFilter = (label, options, valueRender, filterKey) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    ...(filterKey ? { filterKey } : {}),
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

// `scopedJobPostingId` — aşama süzgecinin seçenek kaynağını belirler: bir ilan
// kapsamı açıksa O İLANIN efektif aşamaları, yoksa kiracı varsayılanları. İlana
// özel liste varsa kiracı varsayılanları o ilanda HİÇ geçerli değildir (PARTİ 3),
// yani iki kaynağı birleştirmek hiç eşleşmeyecek bir seçenek sunardı.
const useAtsApplicationsApi = (queryParams, scopedJobPostingId) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [stageOptions, setStageOptions] = useState([]);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/ats-applications?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        // api.js OKUMALARDA reject ETMEZ — gerçek sinyal res.data.status. Eskiden
        // `res.data?.data` doğrudan okunuyordu: 403/500/ağ hatası "kayıt yok" diye
        // çiziliyordu ve kullanıcı eksik listeye bakarak karar veriyordu.
        const { ok, items, pagination: p } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
        setPagination(p);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  // Aşama süzgecinin seçenekleri. Kapsamlı ilan varsa efektif liste ucundan, yoksa
  // kiracı varsayılanlarından (scope[in]=tenant_default — ilana özel satırlar
  // karışırsa kullanıcı başka bir ilanın aşamasını seçer ve liste sessizce boşalır).
  useEffect(() => {
    let alive = true;
    const url = scopedJobPostingId
      ? `/v1/ats-pipeline-stages/effective-for-posting/${scopedJobPostingId}`
      : `/v1/ats-pipeline-stages?${new URLSearchParams({
        'scope[in]': 'tenant_default', pageNumber: 1, pageSize: 50, orderBy: 'orderindex', orderDir: 'asc',
      })}`;
    api.get(url).then((res) => {
      if (!alive) return;
      const { items } = readPage(res);
      setStageOptions(items.map((r) => ({ id: r.id, name: r.name })));
    });
    return () => { alive = false; };
  }, [scopedJobPostingId]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const moveToStage = useCallback(async (id, toStageId, reasonNote) => {
    try {
      await api.post(`/v1/ats-applications/${id}/move-to-stage`, { toStageId, reasonNote });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // `addToPoolId` (SPEC F2): reddet/diskalifiye ile birlikte adayı bir Aday Havuzu'na
  // ekler. Yalnız verildiğinde gövdeye girer — diğer aksiyonların gövdesi DEĞİŞMEZ.
  const changeStatus = useCallback(async (id, action, reason, addToPoolId) => {
    try {
      const body = { action, reason };
      if (addToPoolId) body.addToPoolId = addToPoolId;
      await api.post(`/v1/ats-applications/${id}/status`, body);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const remove = useCallback(async (id) => {
    try {
      await api.delete(`/v1/ats-applications/${id}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // FE-15 — toplu uçlar. Tekil moveToStage/changeStatus'ün AKSİNE boolean DEĞİL,
  // gövdenin kendisini ({succeeded, failed}) döner: sayfa kısmi başarıyı (kaç satır
  // geçti + hangi satır hangi nedenle düştü) GÖSTERMEK zorunda, tek boolean yetmez.
  // Sınır hataları (bulkTargetRequired/bulkLimitExceeded/403) interceptor'ın kendi
  // toast'ıyla bildirilir; burada null dönülür.
  const bulkMoveToStage = useCallback(async (applicationIds, toStageId, reasonNote) => {
    try {
      const res = await api.post('/v1/ats-applications/bulk-move-to-stage', {
        applicationIds, toStageId, reasonNote: reasonNote || undefined,
      });
      refresh();
      return res.data?.data || null;
    } catch {
      return null;
    }
  }, [refresh]);

  const bulkChangeStatus = useCallback(async (applicationIds, action, reason) => {
    try {
      const res = await api.post('/v1/ats-applications/bulk-status', {
        applicationIds, action, reason: reason || undefined,
      });
      refresh();
      return res.data?.data || null;
    } catch {
      return null;
    }
  }, [refresh]);

  // İlan seçici SUNUCUDA arar (title[like]) — 500 ilanlı bir kiracıda bile listenin
  // tamamı istemciye inmez. AsyncGenericSelect sözleşmesi: YALNIZ onSearch okunur.
  const searchJobPostings = useCallback(async (term) => {
    const params = new URLSearchParams({ pageNumber: 1, pageSize: 20 });
    if (term) params.set('title[like]', term);
    const res = await api.get(`/v1/ats-job-postings?${params}`);
    const { items } = readPage(res);
    return items.map((r) => ({ id: r.id, label: r.title }));
  }, []);

  // Kapsam afişi yedekleri: "Başvuruları Gör"/"Başvuruları" router state taşımadan (doğrudan
  // URL, yenileme) gelindiğinde afiş başlığı/adı tek alan okunarak çözülür. GET interceptor'ı
  // reddetmez — hata zarfında data null gelir, boş dönülür.
  const fetchPostingTitle = useCallback(async (jobPostingId) => {
    try {
      const res = await api.get(`/v1/ats-job-postings/${jobPostingId}`);
      return res.data?.data?.title || '';
    } catch {
      return '';
    }
  }, []);

  const fetchCandidateName = useCallback(async (candidateId) => {
    try {
      const res = await api.get(`/v1/ats-candidates/${candidateId}`);
      return res.data?.data?.fullName || '';
    } catch {
      return '';
    }
  }, []);

  // Used by the move-stage dialog: list of effective stages for a given posting.
  const fetchStagesForPosting = useCallback(async (jobPostingId) => {
    try {
      const res = await api.get(`/v1/ats-pipeline-stages/effective-for-posting/${jobPostingId}`);
      return res.data?.data || [];
    } catch {
      return [];
    }
  }, []);

  // Sıralama: backend AtsApplicationDataAccess orderBy switch'i yalnız
  // appliedat | score | candidatename tanır — `sortKey` bunlara eşler; geri kalan kolonlarda
  // ok işareti sahte olurdu, `sort: false`.
  const columns = useMemo(() => {
    const statusLabel = (s) => t(`ats.application.status.${s}`, { defaultValue: s });
    const sourceLabel = (s) => t(`ats.candidate.source.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'jobPostingId', label: 'JobPostingId', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'currentStageId', label: 'CurrentStageId', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'candidateFullName',
        label: t('ats.application.candidate'),
        options: {
          filter: true,
          sort: true,
          sortKey: 'candidatename',
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'candidateName[like]' },
          customBodyRender: (v, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row || !row.priorApplicationCount) return v;
            // En ağır geçmiş durumu etiketle: vazgeçme "diskalifiye" DEĞİLDİR
            // (müşteri geri bildirimi — rozet yanlış "diskalifiye" sinyali veriyordu).
            const priorLabel = row.hasDisqualifiedPrior
              ? t('ats.application.reapplyDisqualified')
              : row.hasRejectedPrior
                ? t('ats.application.reapplyRejected')
                : row.hasWithdrawnPrior
                  ? t('ats.application.reapplyWithdrawn')
                  : t('ats.application.reapply');
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                {v}
                <ReapplyBadge $danger={row.hasDisqualifiedPrior || row.hasRejectedPrior}>
                  {priorLabel}
                </ReapplyBadge>
              </span>
            );
          },
        },
      },
      {
        name: 'candidateEmail',
        label: t('ats.application.email'),
        options: {
          filter: true,
          sort: false,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'candidateEmail[like]' },
        },
      },
      {
        name: 'jobPostingTitle',
        label: t('ats.application.jobPosting'),
        options: { filter: false, sort: false },
      },
      // FE-15 — aşama süzgeci. Sunucu `currentStageId[in]` destekliyor; süzgeç
      // AŞAMA KİMLİĞİ gönderir (ad değil), bu yüzden filterKey açıkça verilir.
      {
        name: 'currentStageName',
        label: t('ats.application.currentStage'),
        options: {
          ...multiSelectFilter(
            t('ats.application.currentStage'),
            stageOptions.map((o) => o.id),
            (id) => stageOptions.find((o) => o.id === id)?.name || id,
            'currentStageId[in]',
          ),
          sort: false,
          customBodyRender: (v) => v || '-',
        },
      },
      {
        name: 'status',
        label: t('ats.application.statusLabel'),
        options: {
          ...multiSelectFilter(t('ats.application.statusLabel'), APPLICATION_STATUSES, statusLabel),
          sort: false,
          customBodyRender: (v, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const flags = parseAutomationFlags(row?.automationFlagsJson);
            const skipped = flags.hireSkipped;
            const pending = flags.corporateEmailPending === true;
            // GN-1 (2026-09-22) — POZİSYON ATLAMASI. Çok kişilik kadroda ilk kabul
            // koltuğu alır; sonrakiler pozisyonsuz açılır ve yöneticileri org ağacından
            // TÜRETİLEMEZ (izin/masraf/hedef talepleri yanlış kişiye düşer). Eskiden bu
            // atlama hiçbir yere yazılmıyordu; artık diğer üç atlama türüyle AYNI
            // rozet/süzgeç yolundan geçer.
            const positionSkipped = flags.positionSkipped;
            if (!skipped && !pending && !positionSkipped) return statusLabel(v);
            // Öncelik: işe alım hiç olmadıysa (skipped) onu göster; olduysa ama eksik
            // kaldıysa kurumsal e-posta; pozisyon atlaması en hafif olandır.
            const label = skipped
              ? t(`ats.hire.skipped.${skipped}`, { defaultValue: t('ats.hire.skipped.other') })
              : pending
                ? t('ats.hire.corporateEmailPending')
                : t(`ats.hire.positionSkipped.${positionSkipped}`, {
                  defaultValue: t('ats.hire.positionSkipped.other'),
                });
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                {statusLabel(v)}
                <HireFlagBadge $danger={!!skipped} title={label}>{label}</HireFlagBadge>
              </span>
            );
          },
        },
      },
      // EO-12 — "Çözüm Bekleyen İşe Alımlar" süzgeci. Sunucu `hireUnresolved[eq]` ile
      // AutomationFlagsJson izlerine bakar; kolonun kendisi gösterilmez (rozet zaten
      // durum sütununda). Tek seçim: filtreyi açmak "yalnız çözüm bekleyenler" demektir.
      {
        name: 'hireUnresolved',
        label: t('ats.hire.unresolvedFilter'),
        options: {
          filter: true,
          sort: false,
          filterType: 'custom',
          filterOptions: {
            defaultOp: 'eq',
            filterKey: 'hireUnresolved[eq]',
            display: (filterList, onChange, index, column) => (
              <FormControl variant="standard" fullWidth>
                <InputLabel>{t('ats.hire.unresolvedFilter')}</InputLabel>
                <Select
                  value={filterList[index]?.[0] || ''}
                  onChange={(e) => {
                    filterList[index] = e.target.value ? [e.target.value] : [];
                    onChange(filterList[index], index, column);
                  }}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  <MenuItem value="true">{t('ats.hire.unresolvedOnly')}</MenuItem>
                </Select>
              </FormControl>
            ),
          },
          customBodyRender: () => null,
          display: 'excluded',
        },
      },
      // Joined from candidate row.
      {
        name: 'source',
        label: t('ats.application.sourceLabel'),
        options: {
          ...multiSelectFilter(t('ats.application.sourceLabel'), APPLICATION_SOURCES, sourceLabel),
          sort: false,
          // The list endpoint doesn't echo this field — server filters via candidate join.
          customBodyRender: () => null,
          display: 'excluded',
        },
      },
      numericFilterColumn('scoreAggregate', t('ats.application.scoreAggregate'), { decimals: 2, sortKey: 'score' }),
      dateFilterColumn('appliedAt', t('ats.application.appliedAt'), lang, { includeTime: true, sortKey: 'appliedat' }),
    ];
  }, [t, lang, data, stageOptions]);

  return {
    fetching, loadFailed, data, pagination, columns, refresh, moveToStage, changeStatus, remove,
    fetchStagesForPosting, fetchPostingTitle, fetchCandidateName,
    bulkMoveToStage, bulkChangeStatus, searchJobPostings, stageOptions,
  };
};

export default useAtsApplicationsApi;
